import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography as MuiTypography,
  List,
  ListItem,
} from "@mui/material";
import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(spacing);

function PoliciesAndProcedures() {
  return (
    <React.Fragment>
      <Helmet title="Policies and Procedures" />

      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12} lg={9} xl={7}>
          <Typography variant="h2" gutterBottom display="inline">
            Authorization Requirements
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/documentation/policies-and-procedures">
              Policies And Procedures
            </Link>
            <Typography>Authorization Requirements</Typography>
          </Breadcrumbs>

          <Divider my={6} />

          <Typography variant="body1" paragraph>
            This document delineates the Authorization Requirements policy of our SaaS medical application to ensure
            compliance with the Health Insurance Portability and Accountability Act (HIPAA). It is crucial for the
            protection of Protected Health Information (PHI) and reinforces our dedication to preserving the privacy and
            security of our users' and patients' health information. This policy outlines the conditions under which PHI
            may be used or disclosed, with a particular focus on instances that require explicit authorization from the
            individual whose information is being handled.
          </Typography>

          <Typography variant="h5" gutterBottom>
            Policy Statement
          </Typography>
          <List>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>General Requirements:</b> Except as otherwise permitted or required by the HIPAA Privacy Rule, our
                  application will not use or disclose PHI without a valid authorization that has been obtained from the
                  individual or their representative. This authorization must specifically detail the use or disclosure
                  permitted and comply with the requirements set forth by HIPAA.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Form and Content of Authorization:</b> An authorization for the use or disclosure of PHI must be in
                  writing, contain specific information required by HIPAA regarding the information to be disclosed, the
                  person(s) authorized to make the disclosure, the recipient of the information, and the purpose of the
                  use or disclosure, as well as an expiration date or event.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Revocation of Authorization:</b> Individuals have the right to revoke their authorization at any
                  time, provided the revocation is in writing. The revocation will not affect any use or disclosures
                  made in reliance on the authorization before the revocation was received.
                </Typography>
              </Box>
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom>
            Implementation
          </Typography>
          <List>
            <ListItem>
              Our team is responsible for ensuring that all uses and disclosures of PHI are in compliance with this
              policy and that all staff are trained on the requirements for obtaining authorization.
            </ListItem>
            <ListItem>
              We will maintain a copy of all authorizations, as well as any revocations, as part of our documentation
              requirements and in accordance with HIPAA's record retention policies.
            </ListItem>
            <ListItem>
              Regular audits will be conducted to verify compliance with this policy and to ensure that no PHI is used
              or disclosed without proper authorization.
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom>
            Exceptions
          </Typography>
          <List>
            <ListItem>
              This policy does not apply to uses and disclosures of PHI for treatment, payment, and health care
              operations (TPO) purposes, as these are permitted under HIPAA without specific authorization.
            </ListItem>
            <ListItem>
              Other exceptions include disclosures required by law, for public health activities, or in emergency
              situations as specified by HIPAA regulations.
            </ListItem>
          </List>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default PoliciesAndProcedures;
